import { useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ObjectRecord } from '@/services/db/object/entity';
import { getObjectsQueryOptions } from '@/services/db/object/queries';
import { queryEncode, safeQueryDecode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import {
  normalizeObjectFilterWire,
  parseObjectFilterWire,
  type ObjectFilterWire,
} from '../object-filter-builder/object-filter-format';
import { useListPicker, type ListFilterCodec, type ListPicker } from './use-list-picker';

const DEFAULT_PAGE_SIZE = 50;
const UNKNOWN_TOTAL = -1;

function objectFilterCodec(objectTypeId: string): ListFilterCodec<ObjectFilterWire> {
  return {
    decode: (encoded) =>
      parseObjectFilterWire(
        normalizeObjectFilterWire(encoded == null ? { and: [] } : safeQueryDecode(encoded), objectTypeId),
      ) ?? { and: [] },
    encode: (serialized) => queryEncode(serialized),
  };
}

export type ObjectPicker = ListPicker<ObjectRecord, ObjectFilterWire> & {
  objects: readonly ObjectRecord[];
  total: number;
  pageSize: number;
  isFetching: boolean;
  hasFilters: boolean;
};

type ObjectPickerOptions = {
  workspaceId: number;
  objectTypeId: string;
  /** The object the caller already chose, which skips the step entirely. */
  preselected?: ObjectRecord;
};

/** The wizard's object list: narrowing, paging, selection, and the list read. */
export function useObjectPicker({ workspaceId, objectTypeId, preselected }: ObjectPickerOptions): ObjectPicker {
  const queryClient = useQueryClient();
  const codec = useMemo(() => objectFilterCodec(objectTypeId), [objectTypeId]);
  const initialSelected = useMemo(() => (preselected == null ? [] : [preselected]), [preselected]);
  const picker = useListPicker<ObjectRecord, ObjectFilterWire>({ initialSelected, codec });
  const { search, filters, page } = picker.params;
  const queryParams = useMemo(
    () => ({
      objectTypeId,
      page,
      name: filters == null && !isEmptyString(search) ? search.toLowerCase() : undefined,
      filters: filters ?? undefined,
    }),
    [objectTypeId, page, search, filters],
  );
  const query = useQuery({
    ...getObjectsQueryOptions(workspaceId, queryParams, queryClient),
    placeholderData: keepPreviousData,
    // The caller already has its object, so the step — and its read — are skipped.
    enabled: preselected == null,
  });

  return {
    ...picker,
    objects: query.data?.objects ?? [],
    total: query.data?.meta.pagination.total ?? UNKNOWN_TOTAL,
    pageSize: query.data?.meta.pagination.size ?? DEFAULT_PAGE_SIZE,
    isFetching: query.isFetching,
    hasFilters: filters != null || !isEmptyString(search),
  };
}
